import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  ElementRef,
  Inject,
  Output,
  EventEmitter,
  Renderer2,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable ,  Subscription ,  BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';

import { SearchWidgetComponent } from 'shared/search-widget/search-widget.component';
import { SearchFormComponent } from 'shared/search/search-form/search-form.component';
import { DefaultModalComponent } from 'shared/modal/default-modal/default-modal.component';
import { BrandService } from 'shared/brand/brand.service';
import { AuthService } from 'shared/auth/auth.service';
import { segmentData } from 'shared/part-types/segment-data-stub';
import { PartTypesSearchService } from 'shared/part-types/search/part-types-search.service';
import { PartTypesSearchModel } from 'shared/part-types/search/part-types-search.model';
import { LoaderComponent } from 'shared/loader/loader.component';

import { columns } from './columns';
import {debounceTime} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'pcdb-modal',
  templateUrl: 'pcdb-modal.component.html',
  styleUrls: ['pcdb-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PCDBModalComponent extends SearchWidgetComponent {

  @ViewChild(DefaultModalComponent, {static: false}) modal: DefaultModalComponent | void;

  @ViewChild('searchResultsContainer', {static: false}) searchResultsContainer: ElementRef | void;

  @ViewChild('searchForm', {static: false}) declare searchForm: SearchFormComponent;

  @ViewChild('pcdbModalLoader', {static: false}) loader: LoaderComponent;

  // assigned part terminology
  assigned: Array<any> = [];
  bulkEdit: boolean = false;

  @Output() hidden: EventEmitter<any> = new EventEmitter();

  @Output() selectPartType = new EventEmitter<{}>();

  items: Array<any> = [];
  segmentData = segmentData;
  declare searchModel: PartTypesSearchModel;
  declare _searchModel: BehaviorSubject<PartTypesSearchModel>;
  declare searchModelObservable: Observable<PartTypesSearchModel>;
  firstLoad: boolean = true;

  categories: Array<any> = [];
  subCategories: Array<any> = [];

  // for current select values
  currentCategory: any;
  currentSubCategory: any;

  loading: boolean = false;

  constructor(
    public searchService: PartTypesSearchService,
    protected brandService: BrandService,
    protected ref: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: any,
    private _renderer: Renderer2,
    private authService: AuthService,
  ) {
    super(
      brandService,
      searchService,
    );
  }

  // getters
  get isEmpty() {
    if (!this.items) { return false; }
    return this.items.length === 0;
  }

  get hasAssigned() {
    return this.assigned.length > 0;
  }

  // public
  public show(partTerminology: any = null, bulkEdit: boolean = false) {
    // clear all data
    this.clearData();
    this.bulkEdit = bulkEdit;
    if (!bulkEdit && partTerminology) {
      partTerminology.thumbnail = '/assets/img/part-type-stub.png';
      this.assigned = [partTerminology];
    }
    if (this.searchForm instanceof SearchFormComponent) {
      this.searchForm.resetValue();
    }
    this.getSearchPartCategories(this.searchModel);
    if (this.modal instanceof DefaultModalComponent) {
      this.modal.show();
    }
  }

  public hide() {
    this.destroySubscriptions();
    this.clearData();
    this.hidden.emit();
    if (this.modal instanceof DefaultModalComponent) {
      this.modal.hide();
    }
  }

  subscribeSearchModel(): Subscription {
    this._searchModel = <BehaviorSubject<PartTypesSearchModel>>new BehaviorSubject(this.searchModel);
    this.searchModelObservable = this._searchModel.asObservable();

    return this.searchModelObservable
    .pipe(debounceTime(900))
    .subscribe((searchModel) => {
      if (this.firstLoad) {
        this.firstLoad = false;
      } else {
        if (searchModel.categoryId) {
          // @ts-ignore
            this.currentCategory = _.find(this.categories, {option: searchModel.categoryId});
        } else {
          this.currentCategory = null;
        }

        if (searchModel.subCategoryId) {
          // @ts-ignore
            this.currentSubCategory = _.find(this.subCategories, {option: searchModel.subCategoryId});
        } else {
          this.currentSubCategory = null;
        }
        this.runSearch(searchModel);
      }
    });
  }

  // handlers
  onCategoryChange(selectedItem: any) {
    if (selectedItem) {
      this.setCategory(selectedItem.option);
    }
  }

  onSubcategoryChange(selectedItem: any) {
    if (selectedItem) {
      this.setSubCategory(selectedItem.option);
    }
  }

  onRowClick(row: any) {
    this.selectPartType.emit(row);
    this.hide();
  }

  onPageChange(page: number) {
    this.setPage(page);
  }

  onClearAll() {
    this.clearData();
  }

  // private
  runSearch(searchModel: PartTypesSearchModel) {
    this.ref.markForCheck();
    this.startLoading();
    this.searchService.runSearch(null, searchModel)
    .subscribe(data => {
      this.ref.markForCheck();
      if (this.searchResultsContainer) {
        this.searchResultsContainer.nativeElement.scrollTop = 0;
      }
      this.items = data.data || [];
      // set stubs for now
      this.firstLoad = false;
      _.forEach(this.items, item => {
        item.thumbnail = '/assets/img/part-type-stub.png';
      });
      this.stopLoading();
    });
  }

  private startLoading() {
    this.loading = true;
    if (this.loader instanceof LoaderComponent) {
      this.loader.show();
    }
  }

  private stopLoading() {
    this.loading = false;
    if (this.loader instanceof LoaderComponent) {
      this.loader.hide();
    }
  }

  private setCategory(categoryId: any) {
    this.searchModel.categoryId = categoryId;
    this.searchModel.subCategoryId = '';
    this.currentSubCategory = null;
    this.subCategories = [];
    this.getSearchPartSubcategories(this.searchModel);
    this._searchModel.next(this.searchModel);
    this.ref.markForCheck();
  }

  private setSubCategory(subCategoryId: any) {
    this.searchModel.subCategoryId = subCategoryId;
    this._searchModel.next(this.searchModel);
    this.ref.markForCheck();
  }

  private setPage(page: any) {
    this.searchModel.currentPage = page;
    this._searchModel.next(this.searchModel);
  }

  private initSearchModel() {
    this.searchModel = new PartTypesSearchModel();
    this.searchModel.itemsPerPage = 10;
    this.searchResultsColumns = columns;

    this.initSubscriptions();
    this._searchModel.next(this.searchModel);
  }

  private getSearchPartCategories(searchModel: PartTypesSearchModel) {
    this.searchService.getSearchPartCategories(searchModel)
    .subscribe((data: Array<{
      option: string,
      title: string,
    }>) => {
      if (_.isArray(data)) {
        return this.categories = data;
      }
    });
  }

  private getSearchPartSubcategories(searchModel: PartTypesSearchModel) {
    this.searchService.getSearchPartSubcategories(searchModel)
    .subscribe(data => this.subCategories = data);
  }

  private clearCategories() {
    this.categories = [];
    this.setCategory('');
  }

  private clearSubcategories() {
    this.subCategories = [];
    this.setSubCategory('');
  }

  private clearData() {
    this.initSearchModel();
    this.items = [];
    this.firstLoad = true;
    this.clearSubcategories();
    this.currentCategory = null;
    this.currentSubCategory = null;
  }
}
